import { z } from "zod";
import { type RankableRow, rankByValue } from "../competition/ranking";
import type { LiftType, SaveResultsBody } from "../competition/results";
import type { IrmCode } from "../competition/vocab";

/**
 * ============================================================
 * TSR WEIGHTLIFTING — the live room protocol and Save Meet
 * ============================================================
 * Ported from the predecessor backend (Wsl-Hono-BE
 * `src/socket/tsr/weightlifting`, `core/live-weightlifting*.ts`) with the
 * event names, rooms and payload shapes UNCHANGED, so the ported TSR
 * weightlifting stations speak to the API exactly as they spoke to v1.
 *
 * A session is N lifters taking three snatches and three clean & jerks on
 * one platform; there is no Team A vs Team B, so weightlifting has its own
 * room (`wl_meet_<meetId>`) instead of the set-based `match_state`.
 *
 *   join_wl_room           { meetId, roomId }                       read  → ack { success, room, degraded }
 *   wl_update_lifter       { meetId, roomId?, participantId, lifter } write → room: wl_lifter_updated (sender excluded)
 *   wl_set_current_lifter  { meetId, roomId, participantId, phase }   write → room: wl_current_lifter_changed,
 *                                                                              wl_referee_updated { reset }, wl_jury_updated { reset }
 *   wl_timer_control       { roomId, action, timerState }             write → room: wl_timer_updated (sender INCLUDED)
 *   wl_referee_decision    { roomId, refereeNo 1–3, decision, override? } write → room: wl_referee_updated
 *   wl_jury_decision       { roomId, juryNo 1–5, decision }           write → room: wl_jury_updated
 *   wl_jury_visibility     { roomId, visible }                        write → room: wl_jury_visibility_updated
 *   join_wl_feed / leave_wl_feed { meetId }                           read  → `wl_feed_<meetId>`: wl_feed_updated
 *
 * `participantId` is the slot's `meet_competitor_id`. Socket payloads keep
 * v1's camelCase; the REST reads (`/api/tsr/weightlifting/…`) go through
 * `ok()` and are snake_case.
 *
 * ── Live state is Redis only ────────────────────────────────────────────────
 * The room (`wl_meet_room:<roomId>`, 24 h, refreshed by every write) holds the
 * lifters' records, the current lifter, the referee / jury lamps and the
 * attempt clock. Postgres sees nothing until the operator presses Save Meet:
 * the CCO maps the room's records with `tsrLiftsSaveBody` (below) and calls
 * the EXISTING `PATCH /api/meets/:id/results` itself. The weigh-in's
 * bodyweight goes through the existing start list (`PUT /api/meets/:id/
 * competitors`, `bodyweight_kg`), also as the operator.
 *
 * ── Para powerlifting ───────────────────────────────────────────────────────
 * v1's para powerlifting stations ran on this same protocol, from a
 * `pl_meet_<meetId>` room, with a record of three `attempts` (one lift, the
 * bench press). The shapes, the room prefix and the BENCH mapping are here;
 * apps/tsr runs its stations as the weightlifting pages in para mode
 * (`/para-powerlifting/…`, `tsr/sport-meets/para-powerlifting`).
 * ============================================================
 */

/** `sports.code` of weightlifting (SEAG) — what `/api/tsr/weightlifting` serves. */
export const TSR_WEIGHTLIFTING_SPORT_CODE = "WLF";

/** Referee chairs per platform (IWF): the three lamps. */
export const TSR_WL_REFEREE_COUNT = 3;
/** Jury seats (protest-review keypads) per meet. v1 kept this in two repos; it lives here now. */
export const TSR_WL_JURY_SEAT_COUNT = 5;
/** Attempts per lift. */
export const TSR_WL_ATTEMPTS = 3;
/** Live room TTL (seconds), refreshed by every write. */
export const TSR_WL_ROOM_TTL_SECONDS = 24 * 60 * 60;
/** The heaviest declared attempt the mapper saves — above any world record, below a typo (OVR's `LIFT_WEIGHT_MAX`). */
export const TSR_WL_WEIGHT_MAX = 500;

/**
 * Every state-changing weightlifting event. They are v1 names, so they do not
 * start `tsr_`: the write guard (apps/api socket/tsr/write-guard.ts) treats
 * these as writes too, and derives the meet from `roomId` when the payload
 * names none (the referee, jury and timer events never did).
 */
export const TSR_WL_WRITE_EVENTS = [
	"wl_update_lifter",
	"wl_set_current_lifter",
	"wl_timer_control",
	"wl_referee_decision",
	"wl_jury_decision",
	"wl_jury_visibility",
] as const;
export type TsrWlWriteEvent = (typeof TSR_WL_WRITE_EVENTS)[number];

/** `GET /tsr/weightlifting/:meetId/scoreboard?view=` — `board` (default): the whole start list; `feed`: the clock and the platform only. */
export const TSR_WL_SCOREBOARD_VIEWS = ["board", "feed"] as const;
export const tsrWlScoreboardQuery = z.object({ view: z.enum(TSR_WL_SCOREBOARD_VIEWS).default("board") });

// ─── Rooms ───────────────────────────────────────────────────────────────────

export const TSR_WL_ROOM_PREFIX = "wl_meet_";
export const TSR_PL_ROOM_PREFIX = "pl_meet_";
const ROOM_PREFIXES = [TSR_WL_ROOM_PREFIX, TSR_PL_ROOM_PREFIX] as const;

export const tsrWlRoomIdFor = (meetId: string): string => `${TSR_WL_ROOM_PREFIX}${meetId}`;
/** Para powerlifting's room — the same protocol under its own prefix (v1 `plRoomIdFor`). */
export const tsrPlRoomIdFor = (meetId: string): string => `${TSR_PL_ROOM_PREFIX}${meetId}`;
/** The store key of a room. */
export const tsrWlRoomKeyFor = (roomId: string): string => `wl_meet_room:${roomId}`;
/** The external feed's room — consumers never join the operator room. */
export const tsrWlFeedRoomFor = (meetId: string): string => `wl_feed_${meetId}`;

export const tsrIsParaRoomId = (roomId: unknown): boolean => typeof roomId === "string" && roomId.startsWith(TSR_PL_ROOM_PREFIX);

/** The meet a room id names (either prefix), or null when it is not a lifting room. */
export function tsrWlMeetIdFrom(roomId: unknown): string | null {
	if (typeof roomId !== "string") return null;
	for (const prefix of ROOM_PREFIXES) if (roomId.startsWith(prefix)) return roomId.slice(prefix.length) || null;
	return null;
}

// ─── Records ─────────────────────────────────────────────────────────────────

/** "" not declared · in-progress declared (on the bar) · good / no-lift judged · not-used passed. */
export type TsrWlAttemptStatus = "" | "in-progress" | "good" | "no-lift" | "not-used";
export const TSR_WL_ATTEMPT_STATUSES = ["", "in-progress", "good", "no-lift", "not-used"] as const satisfies readonly TsrWlAttemptStatus[];

export interface TsrWlAttempt {
	/** Declared barbell weight in kg — free-form text on the station. */
	weight: string;
	status: TsrWlAttemptStatus;
}

export type TsrWlPhase = "snatch" | "cleanJerk";

/** One lifter's live record, shipped whole on every change (`wl_update_lifter`). */
export interface TsrWlLifterData {
	bodyweight: string;
	lotNumber: string;
	category?: string;
	snatch: TsrWlAttempt[];
	cleanJerk: TsrWlAttempt[];
}

/** Para powerlifting's record: one lift, three attempts. */
export interface TsrPlLifterData {
	bodyweight: string;
	lotNumber: string;
	category?: string;
	sportClass?: string;
	attempts: TsrWlAttempt[];
}

export const isTsrWlLifterData = (value: unknown): value is TsrWlLifterData => {
	if (!value || typeof value !== "object") return false;
	const v = value as Partial<TsrWlLifterData>;
	return Array.isArray(v.snatch) && Array.isArray(v.cleanJerk);
};

export const isTsrPlLifterData = (value: unknown): value is TsrPlLifterData => {
	if (!value || typeof value !== "object") return false;
	const v = value as Partial<TsrPlLifterData> & { snatch?: unknown };
	return Array.isArray(v.attempts) && !Array.isArray(v.snatch);
};

/** The clock as the room stores it — the value at the operator's last press, stamped by the SERVER. */
export interface TsrWlStoredTimer {
	seconds: number;
	running: boolean;
	action: string | null;
	/** What Reset returns to (60 / 120 / 600), shared by every timing station. */
	preset?: number;
	/** Server receive stamp (ISO) the countdown extrapolates from. */
	updatedAt: string;
}

export type TsrWlDecision = "good" | "no-lift" | null;
export type TsrWlRoomStatus = "waiting" | "live" | "paused" | "completed";

/** The shared room, as `join_wl_room` returns it (v1 `WLRoomState`). */
export interface TsrWlRoomState {
	id: string;
	meetId: string;
	currentLifterId: string | null;
	phase: string;
	status: TsrWlRoomStatus;
	refereeDecisions: TsrWlDecision[];
	juryDecisions: TsrWlDecision[];
	/** Latched when the attempt clock starts (or a CCO override): the boards show the lamps only then. */
	decisionsRevealed: boolean;
	/** The lamps of THIS attempt were entered by the CCO for the chairs. */
	refereeOverride: boolean;
	/** The jury lamps are on the public leaderboard (a challenge is under review). */
	juryVisible: boolean;
	timer: TsrWlStoredTimer | null;
	/** Every lifter's record, by `meet_competitor_id`. */
	lifters: Record<string, unknown>;
}

// ─── The live feed (`join_wl_feed` → `wl_feed_updated`) ─────────────────────

/** The live clock as the feed sends it: `remaining` is extrapolated to NOW. */
export interface TsrWlFeedTimer {
	remaining: number;
	display: string;
	running: boolean;
	action: string | null;
	expired: boolean;
	publishedRemaining: number;
	publishedAt: string | null;
	ageSeconds: number;
}

/** The read-only live feed of one platform (v1 camelCase wire). Lamps only once revealed. */
export interface TsrWlFeed {
	meetId: string;
	sport: "weightlifting" | "para-powerlifting";
	status: TsrWlRoomStatus;
	phase: TsrWlPhase;
	phaseLabel: string;
	timer: TsrWlFeedTimer;
	currentLifter: {
		participantId: string;
		name: string;
		contingent: string | null;
		lot: string | null;
		bodyweight: string | null;
		/** 1–3; 0 when every attempt is used. */
		attemptNo: number;
		attempt: { weight: string; status: string } | null;
		snatch: TsrWlAttempt[];
		cleanJerk: TsrWlAttempt[];
		attempts?: TsrWlAttempt[];
		best?: number;
	} | null;
	decisions: {
		revealed: boolean;
		referees: TsrWlDecision[];
		jury: TsrWlDecision[];
		override: boolean;
	};
}

// ─── Save Meet: the room's records → the LIFTS save body ─────────────────────

/** One slot of the meet as loaded (`GET /meets/:id`). */
export interface TsrLiftsSaveSlot {
	meet_competitor_id: string;
	version: number;
	irm: IrmCode | null;
	/** Weightlifting's lot number. */
	bib: string | null;
	result_value?: number | null;
	rank?: number | null;
	detail?: Record<string, unknown> | null;
}

export interface TsrLiftsSaveInput {
	/** The room's records by `meet_competitor_id` — either sport's shape; anything else is skipped. */
	lifters: Readonly<Record<string, unknown>>;
	/** Every slot of the meet (filled ones). */
	slots: readonly TsrLiftsSaveSlot[];
}

export interface TsrLift {
	type: LiftType;
	seq: number;
	weight_kg: number;
	good: boolean | null;
}

export interface TsrLiftsSaveResult {
	/** Null when there is nothing to save (no slot has a record with a declared attempt). */
	body: SaveResultsBody | null;
	/** Slots whose lifts are saved. */
	lifters: number;
	/** Records for an id that is not a slot of the meet — not saved. */
	unknown: string[];
}

const OPERATOR_IRMS: ReadonlySet<IrmCode> = new Set(["DNS", "DSQ"]);
/** Keys a LIFTS detail may hold besides `lifts` (imported results) — kept as they are. */
const KEPT_DETAIL_KEYS = ["bodyweight_kg", "lot_no", "entry_total_kg"] as const;

/** Kilograms, or null for anything that is not a positive weight within range. Two decimals at most. */
export function tsrLiftWeight(text: unknown): number | null {
	const n = Number.parseFloat(String(text ?? "").trim());
	if (!Number.isFinite(n) || n <= 0 || n > TSR_WL_WEIGHT_MAX) return null;
	return Math.round(n * 100) / 100;
}

/** One lift's attempts as the LIFTS family stores them. A pass (`not-used`) and an undeclared attempt are not lifts. */
function liftsOf(type: LiftType, attempts: readonly TsrWlAttempt[] | undefined): TsrLift[] {
	const out: TsrLift[] = [];
	(attempts ?? []).slice(0, TSR_WL_ATTEMPTS).forEach((a, i) => {
		if (!a || a.status === "not-used") return;
		const weight = tsrLiftWeight(a.weight);
		if (weight === null) return;
		out.push({ type, seq: i + 1, weight_kg: weight, good: a.status === "good" ? true : a.status === "no-lift" ? false : null });
	});
	return out;
}

/** The record's lifts, in board order. Weightlifting: SNATCH then CLEAN_JERK; para powerlifting: BENCH. */
export function tsrLiftsFromRecord(record: unknown): TsrLift[] | null {
	if (isTsrWlLifterData(record)) return [...liftsOf("SNATCH", record.snatch), ...liftsOf("CLEAN_JERK", record.cleanJerk)];
	if (isTsrPlLifterData(record)) return liftsOf("BENCH", record.attempts);
	return null;
}

/** The heaviest good lift of a type (the earlier attempt on equal weights), or null. */
function bestOf(lifts: readonly TsrLift[], type: LiftType): TsrLift | null {
	let best: TsrLift | null = null;
	for (const l of lifts) {
		if (l.type !== type || l.good !== true) continue;
		if (!best || l.weight_kg > best.weight_kg || (l.weight_kg === best.weight_kg && l.seq < best.seq)) best = l;
	}
	return best;
}

const failed = (lifts: readonly TsrLift[], type: LiftType): boolean => {
	const of = lifts.filter((l) => l.type === type);
	return of.length >= TSR_WL_ATTEMPTS && of.every((l) => l.good === false);
};

/**
 * Total and DNF exactly as OVR's LIFTS board derives them
 * (apps/ovr/src/families/lifts/logic.ts): weightlifting's total is best
 * snatch + best clean & jerk, only once both exist; three no lifts in a
 * section is DNF. Para powerlifting's result is its best bench.
 */
export function tsrLiftsSummary(lifts: readonly TsrLift[]): { total: number | null; failed: boolean; decider: TsrLift | null } {
	if (lifts.some((l) => l.type === "BENCH")) {
		const bench = bestOf(lifts, "BENCH");
		return { total: bench?.weight_kg ?? null, failed: failed(lifts, "BENCH"), decider: bench };
	}
	const sn = bestOf(lifts, "SNATCH");
	const cj = bestOf(lifts, "CLEAN_JERK");
	return {
		total: sn && cj ? Math.round((sn.weight_kg + cj.weight_kg) * 100) / 100 : null,
		failed: failed(lifts, "SNATCH") || failed(lifts, "CLEAN_JERK"),
		decider: cj,
	};
}

const formatKg = (kg: number): string => (Number.isInteger(kg) ? String(kg) : String(Math.round(kg * 100) / 100));

const lotNumber = (bib: string | null): number | null => {
	const b = bib?.trim() ?? "";
	return /^\d+$/.test(b) ? Number(b) : null;
};

const readSavedLifts = (detail: Record<string, unknown> | null | undefined): TsrLift[] => {
	const raw = detail?.["lifts"];
	if (!Array.isArray(raw)) return [];
	return raw.filter(
		(l): l is TsrLift =>
			!!l &&
			typeof l === "object" &&
			typeof (l as TsrLift).weight_kg === "number" &&
			typeof (l as TsrLift).seq === "number" &&
			["SNATCH", "CLEAN_JERK", "BENCH"].includes((l as TsrLift).type),
	);
};

/**
 * Save Meet for a TSR lifting session: every slot with a live record → one
 * LIFTS row of the EXISTING `PATCH /api/meets/:id/results`. Pure: the CCO
 * calls it and sends the body itself, as the signed-in operator, so the
 * route's group / sport / permission check and the slot versions apply
 * exactly as for an OVR save.
 *
 *   detail.lifts    each declared attempt `{ type, seq, weight_kg, good }` —
 *                   good ✓ true, no lift false, declared-not-lifted null; a
 *                   pass and an undeclared attempt are not lifts. The slot's
 *                   imported `bodyweight_kg` / `lot_no` / `entry_total_kg`
 *                   stay (the detail is replaced whole on the server).
 *   result_value    the total (kg), null until there is one
 *   result_display  the IRM, else the total
 *   irm             DNF when a section has three no lifts, lifted again when
 *                   corrected; DNS / DSQ are the operator's and never touched
 *   rank            OVR's `computeLiftsRanks`: by total, ties to who reached
 *                   it first (best C&J weight, its attempt, lot number); a
 *                   slot without a record keeps its saved result, and gets a
 *                   rank-only row only when its place changed
 *
 * Bodyweight and lot are start-list facts (`bodyweight_kg`, `bib`) and are not
 * part of a results save.
 */
export function tsrLiftsSaveBody({ lifters, slots }: TsrLiftsSaveInput): TsrLiftsSaveResult {
	const bySlot = new Map(slots.map((s) => [s.meet_competitor_id, s]));
	const unknown = Object.keys(lifters).filter((id) => !bySlot.has(id));

	type Row = SaveResultsBody["results"][number];
	const rows = new Map<string, Row>();
	const rankables: RankableRow<string>[] = [];

	for (const slot of slots) {
		const lifts = Object.hasOwn(lifters, slot.meet_competitor_id) ? tsrLiftsFromRecord(lifters[slot.meet_competitor_id]) : null;
		if (lifts === null || lifts.length === 0) {
			const saved = readSavedLifts(slot.detail);
			const s = tsrLiftsSummary(saved);
			rankables.push({
				id: slot.meet_competitor_id,
				value: slot.result_value ?? null,
				irm: slot.irm,
				tieBreak: [s.decider?.weight_kg ?? Number.POSITIVE_INFINITY, s.decider?.seq ?? Number.POSITIVE_INFINITY, lotNumber(slot.bib) ?? Number.POSITIVE_INFINITY],
			});
			continue;
		}
		const summary = tsrLiftsSummary(lifts);
		const irm: IrmCode | null =
			slot.irm !== null && OPERATOR_IRMS.has(slot.irm) ? slot.irm : summary.failed ? "DNF" : slot.irm === "DNF" ? null : slot.irm;
		const detail: Record<string, unknown> = {};
		for (const k of KEPT_DETAIL_KEYS) if (slot.detail && slot.detail[k] !== undefined) detail[k] = slot.detail[k];
		detail["lifts"] = lifts.map((l) => ({ ...l }));
		const row: Row = {
			meet_competitor_id: slot.meet_competitor_id,
			version: slot.version,
			detail,
			result_value: summary.total,
			result_display: irm ?? (summary.total === null ? null : formatKg(summary.total)),
		};
		if (irm !== slot.irm) row.irm = irm;
		rows.set(slot.meet_competitor_id, row);
		rankables.push({
			id: slot.meet_competitor_id,
			value: summary.total,
			irm,
			tieBreak: [summary.decider?.weight_kg ?? Number.POSITIVE_INFINITY, summary.decider?.seq ?? Number.POSITIVE_INFINITY, lotNumber(slot.bib) ?? Number.POSITIVE_INFINITY],
		});
	}

	const liftersSaved = rows.size;
	if (liftersSaved === 0) return { body: null, lifters: 0, unknown };

	const ranks = rankByValue(rankables, false);
	for (const slot of slots) {
		const rank = ranks.get(slot.meet_competitor_id) ?? null;
		const row = rows.get(slot.meet_competitor_id);
		if (row) row.rank = rank;
		else if (rank !== (slot.rank ?? null)) rows.set(slot.meet_competitor_id, { meet_competitor_id: slot.meet_competitor_id, version: slot.version, rank });
	}
	return { body: { results: [...rows.values()] }, lifters: liftersSaved, unknown };
}

/**
 * The saved result back as a live record — what a station shows for a meet
 * that was saved (or imported) before this session's room existed. The
 * inverse of `tsrLiftsSaveBody` for the attempts: a lift with `good: null`
 * is declared (`in-progress`). Bodyweight and lot are the start list's.
 *
 * A para powerlifting result (BENCH lifts) comes back in the weightlifting
 * shape the way the live projections read a para record (apps/api
 * `wlFromParaLifter`): its three attempts ride in `snatch`, `cleanJerk` empty.
 */
export function tsrWlRecordFromSaved(
	detail: Record<string, unknown> | null | undefined,
	facts: { bodyweight_kg: number | null; bib: string | null; category?: string },
): TsrWlLifterData | null {
	const lifts = readSavedLifts(detail);
	if (lifts.length === 0) return null;
	const attempts = (type: LiftType): TsrWlAttempt[] =>
		[1, 2, 3].map((seq) => {
			const l = lifts.find((x) => x.type === type && x.seq === seq);
			if (!l) return { weight: "", status: "" };
			return { weight: formatKg(l.weight_kg), status: l.good === true ? "good" : l.good === false ? "no-lift" : "in-progress" };
		});
	const bench = lifts.some((l) => l.type === "BENCH");
	return {
		bodyweight: facts.bodyweight_kg === null ? "" : String(facts.bodyweight_kg),
		lotNumber: facts.bib ?? "",
		...(facts.category ? { category: facts.category } : {}),
		snatch: attempts(bench ? "BENCH" : "SNATCH"),
		cleanJerk: bench ? [] : attempts("CLEAN_JERK"),
	};
}
